import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { Auth0ProviderWithHistory } from './components/auth/Auth0ProviderWithHistory';
import { PrivateRoute } from './components/auth/PrivateRoute';
import { CallbackPage } from './components/auth/CallbackPage';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { HelmetProvider } from 'react-helmet-async';

// Google Analytics import
import { trackPageView } from './utils/analytics';

// Page imports
import HomePage from './pages/HomePage';
import BusinessPage from './pages/BusinessPage'; // NEW: Business persona page
import PrepaidHeroPage from './pages/PrepaidHeroPage';
import HowItWorksPage from './pages/HowItWorksPage';
import AboutUsPage from './pages/AboutUsPage';
import TermsofServicePage from './pages/TermsofServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import AccessibilityPage from './pages/AccessibilityPage';
import AllCirclesPage from './pages/AllCirclesPage';
import MyProfilePage from './pages/MyProfilePage';
import PostCirclePage from './pages/PostCirclePage';
import PlanDetailPage from './pages/PlanDetailPage';
import GetANewNumberPage from './pages/GetANewNumberPage';
import SwitchingCarriersPage from './pages/SwitchingCarriersPage';
import ExistingCustomerPage from './pages/ExistingCustomerPage';
import PaymentPage from './pages/PaymentPage';
import EditPersonalInformationPage from './pages/EditPersonalInformationPage';
import ProfileVerificationPage from './pages/ProfileVerificationPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import CircleRedirectPage from './pages/CircleRedirectPage';
import PaymentHistoryPage from './pages/PaymentHistoryPage';
import ReferralPage from './pages/ReferralPage';
import ProfilePayoutPage from './pages/ProfilePayoutPage';
import SupportPage from './pages/SupportPage';
import NotFoundPage from './pages/NotFoundPage';

// Import transfer components directly
import TransferSwitchingCarriers from './components/MyProfile/Payment/Cancel_SwitchingCarriers/TransferSwitchingCarriers';
import TransferReviewAndConfirm from './components/MyProfile/Payment/Cancel_SwitchingCarriers/TransferReviewAndConfirm';
import TransferPayment from './components/MyProfile/Payment/Cancel_SwitchingCarriers/TransferPayment';
import TransferSuccessPage from './components/MyProfile/Payment/Cancel_SwitchingCarriers/TransferSuccessPage';

// Styles
import './App.css';
import './styles/GlobalEffects.css';


// Initialize Stripe with publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Stripe Elements configuration options
const stripeElementsOptions = {
  mode: 'payment',
  currency: 'usd',
  appearance: {
    theme: 'stripe',
    variables: {
      colorPrimary: '#FF4775',
      colorBackground: '#ffffff',
      colorText: '#111827',
      colorDanger: '#EF4444',
      fontFamily: 'Inter, system-ui, sans-serif',
      borderRadius: '8px',
      spacingUnit: '4px',
    }
  }
};

// Google Analytics Page Tracker Component
function PageTracker() {
  const location = useLocation();

  useEffect(() => {
    // Get readable page names from paths
    const getPageName = (pathname) => {
      const pathMap = {
        '/': 'Home',
        '/business-savings': 'Business Savings', // NEW: Business persona page
        '/business-cell-plan': 'Business Cell Plan', // Alternative route
        '/upgrade-from-prepaid': 'Premium Coverage. Prepaid Price.', // NEW: Prepaid Segmentation persona page
        '/how-it-works': 'How It Works',
        '/about-us': 'About Us',
        '/terms-of-service': 'Terms of Service',
        '/privacy-policy': 'Privacy Policy',
        '/cookie-policy': 'Cookie Policy',
        '/accessibility': 'Accessibility',
        '/all-circles': 'All Circles',
        '/my-profile': 'My Profile',
        '/edit-personalinfo': 'Edit Personal Info',
        '/profile-verification': 'Profile Verification',
        '/change-password': 'Change Password',
        '/onboarding': 'Onboarding',
        '/referral': 'Referral Program',
        '/payout': 'Payout Settings',
        '/support': 'Support',
        '/plandetail': 'Plan Detail',
        '/newnumber': 'Get New Number',
        '/switching-carriers': 'Switching Carriers',
        '/existing-customer': 'Existing Customer',
        '/payment': 'Payment',
        '/payment-history': 'Payment History',
        '/success': 'Payment Success',
        '/callback': 'Auth Callback',
        '/transfer-carrier': 'Transfer - Select Carrier',
        '/transfer-review': 'Transfer - Review',
        '/transfer-payment': 'Transfer - Payment',
        '/transfer-success': 'Transfer - Success',
      };
      
      // Handle dynamic routes
      if (pathname.startsWith('/circle/')) return 'Circle Details';
      if (pathname.startsWith('/c/')) return 'Shared Circle';
      if (pathname.includes('/profile/verification/')) return 'Profile Verification Complete';
      
      return pathMap[pathname] || 'Unknown Page';
    };

    // Track page view on route change
    const pageName = getPageName(location.pathname);
    trackPageView(location.pathname + location.search, pageName);
    
  }, [location]);

  return null; // This component doesn't render anything
}

function App() {
  // Page views are tracked by PageTracker's location effect, which also
  // fires once on first mount; a second call here duplicated the `view`
  // event on first load.

  return (
    <HelmetProvider>
      <Router>
        <Auth0ProviderWithHistory>
          <PageTracker />
          <div className="App">
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              
              {/* NEW: Business Persona Routes */}
              <Route path="/business-savings" element={<BusinessPage />} />
              <Route path="/business-cell-plan" element={<BusinessPage />} />
              <Route path="/upgrade-from-prepaid" element={<PrepaidHeroPage />} />
      
              
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/about-us" element={<AboutUsPage />} />
              <Route path="/terms-of-service" element={<TermsofServicePage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/cookie-policy" element={<CookiePolicyPage />} />
              <Route path="/accessibility" element={<AccessibilityPage />} />
              <Route path="/callback" element={<CallbackPage />} />

              {/* Short URL Redirect - Public */}
              <Route path="/c/:shortCode" element={<CircleRedirectPage />} />

              {/* Public: browse circles and see prices without a login (Fix-first #1) */}
              <Route path="/all-circles" element={<AllCirclesPage />} />

              {/* Protected Routes */}
              <Route
                path="/my-profile"
                element={
                  <PrivateRoute>
                    <MyProfilePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/edit-personalinfo"
                element={
                  <PrivateRoute>
                    <EditPersonalInformationPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile-verification"
                element={
                  <PrivateRoute>
                    <ProfileVerificationPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile/verification/complete"
                element={
                  <PrivateRoute>
                    <ProfileVerificationPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/change-password"
                element={
                  <PrivateRoute>
                    <ChangePasswordPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/onboarding"
                element={
                  <PrivateRoute>
                    <PostCirclePage />
                  </PrivateRoute>
                }
              />
              
              {/* Referral Route */}
              <Route
                path="/referral"
                element={
                  <PrivateRoute>
                    <ReferralPage />
                  </PrivateRoute>
                }
              />

              {/* Payout Route */}
              <Route
                path="/payout"
                element={
                  <PrivateRoute>
                    <ProfilePayoutPage />
                  </PrivateRoute>
                }
              />

              {/* Support Route */}
              <Route
                path="/support"
                element={
                  <PrivateRoute>
                    <SupportPage />
                  </PrivateRoute>
                }
              />

              {/* Circle Routes */}
              {/* Public: see a circle's price without a login (Fix-first #1) */}
              <Route path="/circle/:id" element={<PlanDetailPage />} />
              <Route
                path="/plandetail"
                element={
                  <PrivateRoute>
                    <PlanDetailPage />
                  </PrivateRoute>
                }
              />

              {/* Onboarding Flow Routes */}
              <Route
                path="/newnumber"
                element={
                  <PrivateRoute>
                    <GetANewNumberPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/switching-carriers"
                element={
                  <PrivateRoute>
                    <SwitchingCarriersPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/existing-customer"
                element={
                  <PrivateRoute>
                    <ExistingCustomerPage />
                  </PrivateRoute>
                }
              />

              {/* Payment Routes */}
              <Route
                path="/payment"
                element={
                  <PrivateRoute>
                    <Elements stripe={stripePromise} options={stripeElementsOptions}>
                      <PaymentPage />
                    </Elements>
                  </PrivateRoute>
                }
              />
              <Route
                path="/payment-history"
                element={
                  <PrivateRoute>
                    <PaymentHistoryPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/success"
                element={
                  <PrivateRoute>
                    <PaymentSuccessPage />
                  </PrivateRoute>
                }
              />

              {/* New Transfer Flow Routes - Direct Component References */}
              <Route
                path="/transfer-carrier"
                element={
                  <PrivateRoute>
                    <TransferSwitchingCarriers />
                  </PrivateRoute>
                }
              />
              <Route
                path="/transfer-review"
                element={
                  <PrivateRoute>
                    <TransferReviewAndConfirm />
                  </PrivateRoute>
                }
              />
              <Route
                path="/transfer-payment"
                element={
                  <PrivateRoute>
                    <Elements stripe={stripePromise} options={stripeElementsOptions}>
                      <TransferPayment />
                    </Elements>
                  </PrivateRoute>
                }
              />
              <Route
                path="/transfer-success"
                element={
                  <PrivateRoute>
                    <TransferSuccessPage />
                  </PrivateRoute>
                }
              />

              {/* Catch-all for unmatched routes — public, a mistyped URL must not force a login */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </div>
        </Auth0ProviderWithHistory>
      </Router>
    </HelmetProvider>
  );
}

export default App;